import React from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import "./App.css";
import {
  Route,
  Routes,
  useLocation,
  matchPath,
  Navigate,
} from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import Loader from "./components/StyleComponant/Loader";

// Pages/Components from GateP
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import TalentDashboard from "./pages/TalentDashboard";
import EmployerDashboard from "./pages/EmployerDashboard";
import CompanyProfile from "./components/employer/CompanyProfile";
import AdminDashboard from "./pages/AdminDashboard";
import ProfileBuilder from "./components/talent/profile-builder/ProfileBuilder";
import JobBrowser from "./components/talent/JobBrowser";
import TalentInterviewDashboard from "./components/talent/talentInterview";
import CareerAssistant from "./components/talent/CareerAssistant";
import Report from "./components/talent/Report";
import ExamInterface from "./components/Interviewer/ExamInterface";
import ExamIntroPage from "./components/Interviewer/ExamIntroPage";
import SkillsPassport from "./components/talent/SkillsPassport";
import JobPosting from "./components/employer/JobPosting";
import CandidateManagement from "./components/employer/CandidateManagement";
import InterviewScheduler from "./components/employer/InterviewScheduler";
import EmployerAnalytics from "./components/employer/EmployerAnalytics";
import Evaluation from "./components/employer/Evaluation";
import Analytics from "./components/admin/Analytics";
import UserManagement from "./components/admin/UserManagement";
import TalentHeatmap from "./components/admin/TalentHeatmap";

import { GoldThemeProvider } from "./theme/ThemeContext";
import { NotificationProvider } from "./context/NotificationContext";
import { ChatBotProvider } from "./context/ChatBotContext";

// ProtectedRoute definition
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/db-shiksha/gatep/login" state={{ from: location }} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'talent') return <Navigate to="/db-shiksha/gatep/talent" replace />;
    if (user.role === 'employer') return <Navigate to="/db-shiksha/gatep/employer" replace />;
    if (user.role === 'admin') return <Navigate to="/db-shiksha/gatep/admin" replace />;
    return <Navigate to="/db-shiksha/gatep/login" replace />;
  }

  return children;
};

function GatePdev() {
  return (
    <AuthProvider>
      <GoldThemeProvider>
        <NotificationProvider>
          <ChatBotProvider>
            <MainApp />
          </ChatBotProvider>
        </NotificationProvider>
      </GoldThemeProvider>
    </AuthProvider>
  );
}

function MainApp() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isTalentArea = location.pathname.includes("/db-shiksha/gatep/talent");
  const isEmployerArea = location.pathname.includes(
    "/db-shiksha/gatep/employer",
  );
  const isAdminArea = location.pathname.includes("/db-shiksha/gatep/admin");

  const publicRoutes = ["/login", "/register", ""];

  const isAuthRoute = publicRoutes.some((route) => {
    const cleanPath = location.pathname
      .replace("/db-shiksha/gatep/talent", "")
      .replace("/db-shiksha/gatep/employer", "")
      .replace("/db-shiksha/gatep/admin", "")
      .replace("/db-shiksha/gatep", "");
    return matchPath(route, cleanPath || "/");
  });

  if (auth.loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
          background: "radial-gradient(circle at center, #1a1a1a 0%, #050505 100%)",
          color: "#D4AF37",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 9999,
        }}
      >
        <Loader size="3.5rem" color="#D4AF37" />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 300, letterSpacing: '2px' }}>
          INITIALIZING SECURE SESSION
        </Typography>
      </Box>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route path="\login" element={<Login />} />
        <Route path="\register" element={<Register />} />

        {/* Talent Routes */}
        {isTalentArea && (
          <>
            <Route
              path="talent"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <TalentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/profile"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <ProfileBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/jobs"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <JobBrowser />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/interviews"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <TalentInterviewDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/assistant"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <CareerAssistant />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/exam"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <ExamInterface />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/passport"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <SkillsPassport />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/report"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <Report />
                </ProtectedRoute>
              }
            />
            <Route
              path="talent/exam-intro"
              element={
                <ProtectedRoute allowedRoles={["talent"]}>
                  <ExamIntroPage />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* Employer Routes */}
        {isEmployerArea && (
          <>
            <Route
              path="employer"
              element={
                <ProtectedRoute allowedRoles={["employer"]}>
                  <EmployerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="employer/company-profile"
              element={
                <ProtectedRoute allowedRoles={["employer"]}>
                  <CompanyProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="employer/post-job"
              element={
                <ProtectedRoute allowedRoles={["employer"]}>
                  <JobPosting />
                </ProtectedRoute>
              }
            />
            <Route
              path="employer/candidates"
              element={
                <ProtectedRoute allowedRoles={["employer"]}>
                  <CandidateManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="employer/interviews"
              element={
                <ProtectedRoute allowedRoles={["employer"]}>
                  <InterviewScheduler />
                </ProtectedRoute>
              }
            />
            <Route
              path="employer/analytics"
              element={
                <ProtectedRoute allowedRoles={["employer"]}>
                  <EmployerAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="employer/evaluation"
              element={
                <ProtectedRoute allowedRoles={["employer"]}>
                  <Evaluation />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* Admin Routes */}
        {isAdminArea && (
          <>
            <Route
              path="admin"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/analytics"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/heatmap"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <TalentHeatmap />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* Root Redirect to Login as requested */}
        <Route path="" element={<Navigate to="login" replace />} />

        {/* Catch-all for landing */}
        <Route path="*" element={<Navigate to="login" replace />} />
      </Routes>
    </div>
  );
}

export default GatePdev;
